import { Flex } from '@mantine/core';
import { modals } from '@mantine/modals';
import { toast } from 'react-toastify';

import { TransitGroup, TransitStatus } from '@e-origin/shared';

import { blueCheckMarkIcon, dangerIcon, deleteIcon, finalizeIcon, viewIcon } from '@assets';
import { TableActions, TableColumnProps } from '@components';

export const useGoodsItemsDetailsColumns = ({ form, houseRef, openGoodsItem, editDisabled, editDisabledConditionalForArrival, selectedRow }) => {
  const handleItemDelete = (row) => {
    const { consignmentItemIdx = -1 } = row || {};
    form.setFieldValue(
      `Consignment.HouseConsignment.${houseRef}.ConsignmentItem`,
      form.values.Consignment.HouseConsignment[houseRef].ConsignmentItem.filter((_, idx) => idx !== consignmentItemIdx),
    );
    selectedRow.current = null;
  };

  const openDeleteGoodsItem = (row) => {
    modals.openConfirmModal({
      title: 'Confirm item delete',
      children: <div>Are you sure you want to delete this goods item?</div>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => handleItemDelete(row),
    });
  };

  const actionList = (row, index) => {
    const isUnloadPermission = form?.values?.generalInfo?.status === TransitStatus.UNLOAD_PERMISSION;
    const path = `Consignment.HouseConsignment.${houseRef}.ConsignmentItem.${index}.isMissing`;

    const consignmentItems = form?.values?.Consignment?.HouseConsignment[houseRef]?.ConsignmentItem;
    const isMissing = row.isMissing;
    const isItemMissingAllowed = () => isUnloadPermission && row.receivedFromUnloadPermission && !isMissing;
    const isItemFoundAllowed = () => isUnloadPermission && row.receivedFromUnloadPermission && !!isMissing;

    return [
      {
        if: isItemMissingAllowed(),
        icon: dangerIcon,
        text: 'Item missing',
        onClick: () => {
          if (consignmentItems.filter((item) => !item.isMissing).length === 1) {
            form.setFieldValue(`Consignment.HouseConsignment.${houseRef}.isMissing`, true);
          }
          form.setFieldValue(path, true);

          toast.success('Item marked as missing!');
        },
      },
      {
        if: isItemFoundAllowed(),
        icon: finalizeIcon,
        text: 'Item found',
        onClick: () => {
          if (consignmentItems.filter((item) => item.isMissing).length === consignmentItems.length) {
            form.setFieldValue(`Consignment.HouseConsignment.${houseRef}.isMissing`, false);
          }
          form.setFieldValue(path, false);
          toast.success('Item marked as found!');
        },
      },
      {
        icon: viewIcon,
        text: 'Edit',
        onClick: () => {
          openGoodsItem(row);
        },
        disableAfterClick: false,
      },
      {
        if: (!editDisabled || !editDisabledConditionalForArrival) && !row.receivedFromUnloadPermission,
        icon: deleteIcon,
        text: 'Delete',
        onClick: () => openDeleteGoodsItem(row),
        disableAfterClick: false,
      },
    ];
  };

  const columns: TableColumnProps<any>[] = [
    {
      name: 'Declaration Goods Item Number',
      cell: (row) => row.declarationGoodsItemNumber,
    },
    {
      name: 'Goods Item Number',
      cell: (row) => row.goodsItemNumber,
    },
    {
      // todo: hide can be also a function
      hide: form?.values?.generalInfo.group === TransitGroup.DEPARTURE,
      name: 'Is missing',
      cell: (row) =>
        row.isMissing ? (
          <Flex align="center" justify="center" h={24} w={24}>
            <img src={blueCheckMarkIcon} alt="Is missing" />
          </Flex>
        ) : (
          ''
        ),
    },
    {
      name: 'Description of goods',
      cell: (row) => row.descriptionOfGoods,
    },
    {
      name: 'HS code',
      cell: (row) => row.hsCode,
    },
    {
      name: 'Gross mass',
      cell: (row) => row.grossMass,
    },
    {
      name: 'Type of packages',
      cell: (row) => row.typeOfPackages,
    },
    {
      name: 'Number of packages',
      cell: (row) => row.numberOfPackages,
    },
    {
      name: 'Marking',
      cell: (row) => row.shippingMarks,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row, rowIndex) => (
        <TableActions
          actions={actionList(row, rowIndex)}
          secondaryActions={[]}
          onClick={() => {
            selectedRow.current = row;
          }}
          disableAfterClick={true}
        />
      ),
    },
  ];

  return { columns };
};
